import { ref, toValue, onMounted, onBeforeUnmount } from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'

/**
 * Blocks navigation away from a dirty editor and asks what to do first: save and
 * leave, discard, or stay. Also raises the browser's own prompt on reload/tab close.
 *
 * `onBeforeRouteUpdate` fires for every navigation that keeps this component's route
 * record — including a child-route switch (e.g. Changes <-> Results) — so `isSameTarget`
 * decides which updates are real "leaving" moves; those it accepts pass through silently.
 */
export const useUnsavedChangesGuard = ({ isDirty, onSave, isSameTarget = () => false }) => {
  const isDialogOpen = ref(false)
  const isSaving = ref(false)
  let resolveDecision = null

  const ask = () => new Promise(resolve => {
    // A second navigation while the dialog is open supersedes the first: staying is the safe answer for it.
    resolveDecision?.('stay')
    resolveDecision = resolve
    isDialogOpen.value = true
  })
  const decide = decision => {
    const resolve = resolveDecision
    resolveDecision = null
    resolve?.(decision)
  }

  const guard = async (to, from) => {
    if (!toValue(isDirty) || isSameTarget(to, from)) return true

    const decision = await ask()

    if (decision === 'discard') {
      isDialogOpen.value = false
      return true
    }
    if (decision === 'save') {
      isSaving.value = true
      try {
        await onSave()
        return true
      } catch {
        return false
      } finally {
        isSaving.value = false
        isDialogOpen.value = false
      }
    }

    isDialogOpen.value = false
    return false
  }

  const onBeforeUnload = event => {
    if (!toValue(isDirty)) return
    event.preventDefault()
    event.returnValue = ''
  }

  const onSaveAndLeave = () => decide('save')
  const onDiscard = () => decide('discard')
  const onStay = () => decide('stay')

  onBeforeRouteLeave(guard)
  onBeforeRouteUpdate(guard)

  onMounted(() => {
    window.addEventListener('beforeunload', onBeforeUnload)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', onBeforeUnload)
  })

  return { isDialogOpen, isSaving, onSaveAndLeave, onDiscard, onStay }
}
